import { IconAlertTriangle, IconInbox, IconLock } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccessDenied } from './access-denied.js';
import classes from './EmptyState.module.css';

export interface EmptyStateProps {
  /** Dairedeki ikon; verilmezse tona göre (kutu ya da uyarı üçgeni). */
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** "Try again", "Clear filters" gibi eylem düğmesi. */
  action?: ReactNode;
  /** `danger`: veri okunamadı; `neutral`: kayıt yok ya da eşleşme yok. */
  tone?: 'neutral' | 'danger';
  className?: string | undefined;
}

/**
 * Boş, hata ve eşleşme yok durumu (M6f G1-05; plan §0.6): ikon dairesi, başlık, açıklama ve eylem.
 * Bütün ekranlar düz metin yerine bunu kullanır. Hata `alert`, boş durum `status` olarak duyurulur.
 * Sayfanın okuması `FORBIDDEN` döndüyse hata durumu "yeniden dene" yerine yetki yok boş durumu
 * olur (ADR 0067 §6): yeniden denemek sonucu değiştirmez.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'neutral',
  className,
}: EmptyStateProps) {
  const { t } = useTranslation();
  const denied = useAccessDenied();
  if (tone === 'danger' && denied) {
    return (
      <div
        className={[classes.root, className].filter(Boolean).join(' ')}
        data-tone="neutral"
        data-access="denied"
        role="status"
      >
        <span className={classes.icon} aria-hidden>
          <IconLock size={22} stroke={1.8} />
        </span>
        <p className={classes.title}>{t('common.accessDenied.title')}</p>
        <p className={classes.description}>{t('common.accessDenied.description')}</p>
      </div>
    );
  }
  const glyph =
    icon ??
    (tone === 'danger' ? (
      <IconAlertTriangle size={22} stroke={1.8} />
    ) : (
      <IconInbox size={22} stroke={1.8} />
    ));
  return (
    <div
      className={[classes.root, className].filter(Boolean).join(' ')}
      data-tone={tone}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <span className={classes.icon} aria-hidden>
        {glyph}
      </span>
      <p className={classes.title}>{title}</p>
      {description ? <p className={classes.description}>{description}</p> : null}
      {action ? <div className={classes.action}>{action}</div> : null}
    </div>
  );
}
